import { c as _c } from "react/compiler-runtime";
import { Accordion } from '@ark-ui/react/accordion';
import { Icon } from '@repo/design-system/components/ds/icon';
import { css, cva } from '@repo/panda/css';
import type { SystemStyleObject } from '@repo/panda/types';
import type { ReactNode } from 'react';
export interface AccordionProps {
  items: AccordionItemProps[];
  variant?: AccordionVariants['variant'];
  size?: AccordionVariants['size'];
  className?: SystemStyleObject;
  multiple?: boolean;
  collapsible?: boolean;
  defaultValue?: string[];
}
export interface AccordionItemProps {
  value: string;
  title: ReactNode;
  children: ReactNode;
  disabled?: boolean;
}
type AccordionVariants = {
  variant: 'dark' | 'light' | 'ghost';
  size: 'sm' | 'md' | 'lg';
};
const accordionStyles = cva({
  base: {
    width: 'full',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '6',
    overflow: 'hidden'
  },
  variants: {
    variant: {
      dark: {
        borderColor: 'grey.700',
        backgroundColor: 'grey.800'
      },
      light: {
        borderColor: 'grey.200',
        backgroundColor: 'brand.white'
      },
      ghost: {
        borderColor: 'white/10',
        backgroundColor: 'white/5'
      }
    },
    size: {
      sm: {
        fontSize: 'md'
      },
      md: {
        fontSize: 'lg'
      },
      lg: {
        fontSize: 'xl'
      }
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md'
  }
});
const accordionItemStyles = cva({
  base: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  variants: {
    variant: {
      dark: {
        borderColor: 'grey.700'
      },
      light: {
        borderColor: 'grey.200'
      },
      ghost: {
        borderColor: 'white/10',
        backgroundColor: 'white/5'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const accordionTriggerStyles = cva({
  base: {
    width: 'full',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
    cursor: 'pointer',
    fontWeight: 'medium',
    outline: 'none',
    '&:hover': {
      opacity: 0.8
    },
    '&[data-disabled]': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  variants: {
    variant: {
      dark: {
        backgroundColor: 'grey.800',
        color: 'white',
        '&:hover': {
          backgroundColor: 'grey.700'
        }
      },
      light: {
        backgroundColor: 'grey.50',
        color: 'black',
        '&:hover': {
          backgroundColor: 'grey.100'
        }
      },
      ghost: {
        backgroundColor: 'transparent',
        color: 'white',
        '&:hover': {
          backgroundColor: 'white/5'
        }
      }
    },
    size: {
      sm: {
        padding: '8'
      },
      md: {
        padding: '12'
      },
      lg: {
        padding: '16'
      }
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md'
  }
});
const accordionContentStyles = cva({
  base: {
    overflow: 'hidden'
  },
  variants: {
    variant: {
      dark: {
        backgroundColor: 'grey.900'
      },
      light: {
        backgroundColor: 'brand.white'
      },
      ghost: {
        backgroundColor: 'transparent'
      }
    },
    size: {
      sm: {
        padding: '8'
      },
      md: {
        padding: '12'
      },
      lg: {
        padding: '16'
      }
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md'
  }
});
export const AccordionComponent = t0 => {
  const $ = _c(17);
  const {
    items,
    variant: t1,
    size: t2,
    className,
    multiple: t3,
    collapsible: t4,
    defaultValue
  } = t0;
  const variant = t1 === undefined ? "dark" : t1;
  const size = t2 === undefined ? "md" : t2;
  const multiple = t3 === undefined ? false : t3;
  const collapsible = t4 === undefined ? true : t4;
  let t5;
  if ($[0] !== className || $[1] !== size || $[2] !== variant) {
    t5 = css(accordionStyles.raw({
      variant,
      size
    }), className);
    $[0] = className;
    $[1] = size;
    $[2] = variant;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const rootStyle = t5;
  let t6;
  if ($[4] !== items || $[5] !== size || $[6] !== variant) {
    let t7;
    if ($[8] !== size || $[9] !== variant) {
      t7 = item => <Accordion.Item key={item.value} value={item.value} disabled={item.disabled} className={accordionItemStyles({
        variant
      })}><Accordion.ItemTrigger className={accordionTriggerStyles({
          variant,
          size
        })}>{item.title}<Accordion.ItemIndicator><Icon icon="expand_more" size="sm" className={css({
              "&[data-state=\"open\"]": {
                transform: "rotate(180deg)"
              }
            })} decorative={true} /></Accordion.ItemIndicator></Accordion.ItemTrigger><Accordion.ItemContent className={accordionContentStyles({
          variant,
          size
        })}>{item.children}</Accordion.ItemContent></Accordion.Item>;
      $[8] = size;
      $[9] = variant;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    t6 = items.map(t7);
    $[4] = items;
    $[5] = size;
    $[6] = variant;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[11] !== collapsible || $[12] !== defaultValue || $[13] !== multiple || $[14] !== rootStyle || $[15] !== t6) {
    t7 = <Accordion.Root className={rootStyle} multiple={multiple} collapsible={collapsible} defaultValue={defaultValue}>{t6}</Accordion.Root>;
    $[11] = collapsible;
    $[12] = defaultValue;
    $[13] = multiple;
    $[14] = rootStyle;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};